import { createAsyncThunk } from '@reduxjs/toolkit';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import {
  ApiNumericSubField,
  isChartSpec,
  isValidArtifact,
  toNumericSubFields,
} from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import {
  LubavSessionType,
  DOCUMENT_TYPES,
  SearchedUser,
  AttachedNuggetData,
  AttachedFileData,
  LubavOfficeHoursSubsessionType,
  OfficeHoursMessage,
  DocumentType,
  StructuredSessionId,
} from '@backrs_ui/grow-ui/src/models/models';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';
import { userDocumentsApi, UserDocuments } from '@backrs_ui/grow-ui/src/redux/user/user.lubav.api';
import { hydrateSessionResources } from '@backrs_ui/grow-ui/src/utils/streamUtils';

import {
  setCoachingUser,
  setOfficeHoursMessages,
  resetCurrentSession,
  resetSuggestedPrompts,
  setIsStreaming,
  setIsLoading,
  clearActiveTools,
  setSessionId,
  setSessionTitle,
} from './coachLubav';
import { officeHoursApi, GetOfficeHoursSessionDataResponse } from './coachLubav.api';
import {
  resetBoxData,
  setIsLoadingResources,
  setToolTriggered,
  setStreamingComplete,
  setResourcesContent,
  setArtifact,
} from '../boxData/boxData';
import { createSessionAction } from '../lubavSessionState/lubavSessionState.utils';

const getSessionTypeFromPath = (
  path: string,
  solutionSessionName?: string | null,
  officeHoursId?: string,
): string | undefined => {
  return (
    officeHoursId ||
    solutionSessionName ||
    (path === APP_ROUTE.OFFICE_HOURS && 'office_hours') ||
    (path === APP_ROUTE.HOME && 'office_hours') ||
    undefined
  );
};

export const initiateSession = createAsyncThunk(
  'coachLubav/initiateSession',
  async (
    {
      path,
      solutionSessionName,
      officeHoursId,
      activeSession,
      coachingUserId,
    }: {
      path: string;
      solutionSessionName?: string | null;
      officeHoursId?: string;
      activeSession: LubavSessionType | null;
      coachingUserId?: number;
    },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const sessionType = getSessionTypeFromPath(path, solutionSessionName, officeHoursId);

      if (!sessionType) return;

      const sessionId = await dispatch(
        officeHoursApi.endpoints.initiateSession.initiate({
          session_type: sessionType,
          coaching_user_id: coachingUserId,
        }),
      ).unwrap();

      const result = {
        sessionId,
        sessionType,
        activeSession,
      };

      return result;
    } catch (error) {
      return rejectWithValue(`Failed to fetch session id with error: ${error}`);
    }
  },
);

export const checkCoachingUserDocuments = createAsyncThunk<
  UserDocuments,
  { userId: string; forceRefetch?: boolean }
>(
  'coachLubav/checkCoachingUserDocuments',
  async ({ userId, forceRefetch }, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(
        userDocumentsApi.endpoints.checkUserDocuments.initiate(
          {
            documents: Object.values(DOCUMENT_TYPES),
            userId,
          },
          { forceRefetch: forceRefetch ?? false },
        ),
      ).unwrap();

      return result;
    } catch (error) {
      return rejectWithValue('Failed to check user resume: ' + error);
    }
  },
);

export const setCoachingUserWithDocuments = createAsyncThunk<
  undefined,
  { user: SearchedUser | undefined; sessionType: LubavSessionType },
  { state: RootState }
>('coachLubav/setCoachingUserWithDocuments', async ({ user, sessionType }, { dispatch }) => {
  dispatch(createSessionAction(setCoachingUser, sessionType)(user));

  if (user) {
    const documents = await dispatch(checkCoachingUserDocuments({ userId: user.id })).unwrap();

    dispatch(createSessionAction(setCoachingUser, sessionType)({ ...user, documents }));
  }

  return undefined;
});

export const resetOfficeHoursSession = createAsyncThunk<
  undefined,
  { activeSession: LubavSessionType },
  { state: RootState }
>('coachLubav/resetOfficeHoursSession', async ({ activeSession }, { dispatch }) => {
  dispatch(createSessionAction(setOfficeHoursMessages, activeSession)([]));
  dispatch(createSessionAction(resetCurrentSession, activeSession)(undefined));
  dispatch(createSessionAction(resetSuggestedPrompts, activeSession)(undefined));
  dispatch(createSessionAction(resetBoxData, activeSession)(undefined));
  dispatch(createSessionAction(setIsStreaming, activeSession)(false));
  dispatch(createSessionAction(setIsLoading, activeSession)(false));

  return undefined;
});

const transformApiMessageToOfficeHoursMessage = (
  msg: {
    id: number;
    sender: 'ai' | 'human';
    text: string;
    question?: string;
    type?: string;
    options?: string[];
    exclusive_options?: string[];
    option_descriptions?: Record<string, string>;
    sub_fields?: ApiNumericSubField[];
    default_value?: string;
    required?: boolean;
    documents_requested?: string;
    include_freeform?: boolean;
    charts?: unknown[];
  },
  documents?: UserDocuments,
): OfficeHoursMessage => {
  let text = msg.text;

  if (msg.question) {
    try {
      const parsed = JSON.parse(msg.question);

      text = parsed.response || msg.text;
    } catch {
      // If not valid JSON, use as-is
      text = msg.question;
    }
  }

  let componentType: OfficeHoursMessage['componentType'];

  const includeFreeform = msg.include_freeform ?? true;
  const required = msg.required;

  if ((msg.type === 'single_select' || msg.type === 'slider') && msg.options) {
    componentType = {
      type: 'SINGLE_SELECT' as const,
      options: msg.options,
      includeFreeform,
      required,
    };
  } else if (msg.type === 'multi_select' && msg.options) {
    componentType = {
      type: 'MULTI_SELECT' as const,
      options: msg.options,
      exclusiveOptions: msg.exclusive_options,
      includeFreeform,
      required,
    };
  } else if (msg.type === 'single_select_described' && msg.options) {
    componentType = {
      type: 'SINGLE_SELECT_DESCRIBED' as const,
      options: msg.options,
      optionDescriptions: msg.option_descriptions,
      defaultValue: msg.default_value,
      includeFreeform,
      required,
    };
  } else if (msg.type === 'numeric' && msg.default_value) {
    // Confirming a value already on file answers with that value, not the button's label.
    componentType = {
      type: 'SINGLE_SELECT' as const,
      options: ['Correct'],
      answerValue: msg.default_value,
      includeFreeform: true,
      required,
    };
  } else if (msg.type === 'multi_numeric' && (msg.sub_fields || msg.options)) {
    componentType = {
      type: 'MULTI_NUMERIC' as const,
      options: msg.options ?? [],
      subFields: toNumericSubFields(msg.sub_fields),
      includeFreeform: msg.include_freeform ?? true,
      required,
    };
  } else if (msg.type === 'file_upload') {
    const docType = (msg.documents_requested as DocumentType) || 'resume';
    const hasExistingDocument = documents?.[docType]?.some((doc) => doc.filename);

    componentType = hasExistingDocument
      ? { type: 'CONFIRM_DOCUMENT' as const, documentType: docType, includeFreeform }
      : { type: 'FILE_UPLOAD' as const, documentType: docType, includeFreeform };
  } else if (msg.type === 'career_paths') {
    componentType = {
      type: 'CAREER_PATH_SELECTOR' as const,
      includeFreeform,
      required,
    };
  } else if (msg.type === 'college_types') {
    componentType = {
      type: 'COLLEGE_TYPE_SELECTOR' as const,
      includeFreeform,
      required,
    };
  } else if ((msg.type === 'freeform' || msg.type === 'numeric') && required !== true) {
    componentType = { type: 'SKIP_ONLY' as const };
  }

  const charts = msg.charts?.filter(isChartSpec);

  return {
    id: String(msg.id),
    sender: msg.sender === 'human' ? 'user' : msg.sender,
    text,
    componentType,
    ...(charts?.length ? { charts } : {}),
  };
};

export const loadOfficeHoursSessionData = createAsyncThunk(
  'coachLubav/loadOfficeHoursSessionData',
  async (
    {
      sessionData,
      activeSession,
    }: {
      sessionData: GetOfficeHoursSessionDataResponse;
      activeSession: LubavSessionType;
    },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    const documents = state.user.documents;

    dispatch(createSessionAction(setSessionId, activeSession)(sessionData.session_id));

    if (sessionData.session_title) {
      dispatch(createSessionAction(setSessionTitle, activeSession)(sessionData.session_title));
    }

    if (sessionData.messages) {
      const officeHoursMessages = sessionData.messages.map((msg) => {
        const baseMessage = transformApiMessageToOfficeHoursMessage(msg, documents);

        const attachedNuggets = (msg as { attached_nuggets?: AttachedNuggetData[] })
          .attached_nuggets;
        const attachedFiles = (msg as { attachments?: AttachedFileData[] }).attachments;

        if (attachedFiles && attachedFiles.length > 0) {
          return {
            ...baseMessage,
            componentType: {
              type: 'ATTACHED_FILES' as const,
              attachments: attachedFiles,
            },
          };
        }

        if (attachedNuggets && attachedNuggets.length > 0) {
          return {
            ...baseMessage,
            componentType: {
              type: 'ATTACHED_NUGGETS' as const,
              nuggets: attachedNuggets,
            },
          };
        }

        return baseMessage;
      });

      dispatch(createSessionAction(setOfficeHoursMessages, activeSession)(officeHoursMessages));
    }

    // load nuggets with tab/isFeatured based on subsession
    if (sessionData.additional_resources) {
      const resources = hydrateSessionResources(
        sessionData.additional_resources,
        // fall back to the session id so a nugget-output session features its output on resume
        (sessionData.subsession_type ?? activeSession) as
          | LubavOfficeHoursSubsessionType
          | StructuredSessionId
          | undefined,
        sessionData.featured_nugget_types,
      );

      dispatch(createSessionAction(setResourcesContent, activeSession)(resources));
    }

    if (sessionData.artifact) {
      if (isValidArtifact(sessionData.artifact)) {
        dispatch(createSessionAction(setArtifact, activeSession)(sessionData.artifact));
      } else {
        console.warn(
          'loadOfficeHoursSessionData: dropping artifact with unrecognized kind',
          sessionData.artifact,
        );
      }
    }

    dispatch(createSessionAction(setStreamingComplete, activeSession)(false));

    dispatch(createSessionAction(setIsLoading, activeSession)(false));

    return sessionData;
  },
);

export const resetStreamingState = createAsyncThunk<
  undefined,
  { activeSession: LubavSessionType },
  { state: RootState }
>('coachLubav/resetStreamingState', async ({ activeSession }, { dispatch }) => {
  dispatch(createSessionAction(setIsLoading, activeSession)(false));
  dispatch(createSessionAction(setIsStreaming, activeSession)(false));
  dispatch(createSessionAction(setIsLoadingResources, activeSession)(false));
  dispatch(createSessionAction(clearActiveTools, activeSession)(undefined));
  dispatch(createSessionAction(setToolTriggered, activeSession)(false));

  return undefined;
});
